import React, { useState } from 'react';
import './ShareQuote.css';
import APP_CONFIG from '../config/appConfig';
import { saveQuote } from '../lib/share';

/**
 * "Send this to the customer" — captures who the quote is for, saves it, and
 * hands back a link that opens a read-only version with these exact prices.
 */
const ShareQuote = ({ quoteState, priced, settingsValues }) => {
    const [meta, setMeta] = useState({ clientName: '', companyName: '', preparedBy: '', note: '' });
    const [status, setStatus] = useState('idle'); // idle | saving | done | error
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const recommended = APP_CONFIG.packages[priced.recommendedTier].name;

    const handleShare = async () => {
        if (!meta.companyName.trim()) {
            setStatus('error');
            setResult({ error: 'Add the company name so the quote can be identified later.' });
            return;
        }
        setStatus('saving');
        const saved = await saveQuote(quoteState, {
            ...meta,
            recommendedTier: priced.recommendedTier,
            monthlyTotal: priced.tierCosts[priced.recommendedTier].monthlyRecurringCost,
            createdAt: new Date().toISOString(),
            // Pin the calibration this quote was priced on. Without it, editing
            // a bitrate later would silently change the price on a quote a
            // customer has already been sent.
            calculationSettings: settingsValues
        });
        setResult(saved);
        setStatus('done');
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(result.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            // Clipboard can be blocked; the input below is selectable as a fallback.
            setCopied(false);
        }
    };

    return (
        <div className="share-quote">
            <h2>Send this quote to the customer</h2>
            <p className="share-lead">
                Creates a read-only page with exactly these prices — the {recommended} recommendation,
                the breakdown, and the projections. Nothing on it can be edited by the recipient.
            </p>

            {status !== 'done' ? (
                <>
                    <div className="share-fields">
                        <label>
                            Company <span className="req">required</span>
                            <input
                                type="text"
                                value={meta.companyName}
                                placeholder="Acme Streaming Ltd"
                                onChange={(e) => setMeta({ ...meta, companyName: e.target.value })}
                            />
                        </label>
                        <label>
                            Contact name
                            <input
                                type="text"
                                value={meta.clientName}
                                placeholder="Who you spoke to"
                                onChange={(e) => setMeta({ ...meta, clientName: e.target.value })}
                            />
                        </label>
                        <label>
                            Prepared by
                            <input
                                type="text"
                                value={meta.preparedBy}
                                placeholder="Your name"
                                onChange={(e) => setMeta({ ...meta, preparedBy: e.target.value })}
                            />
                        </label>
                        <label className="span-2">
                            Note for the customer
                            <textarea
                                rows="2"
                                value={meta.note}
                                placeholder="Optional — appears at the top of their page"
                                onChange={(e) => setMeta({ ...meta, note: e.target.value })}
                            />
                        </label>
                    </div>

                    {status === 'error' && result?.error && (
                        <p className="share-error">{result.error}</p>
                    )}

                    <button
                        type="button"
                        className="share-btn"
                        onClick={handleShare}
                        disabled={status === 'saving'}
                    >
                        {status === 'saving' ? 'Creating link…' : 'Create shareable link'}
                    </button>
                </>
            ) : (
                <div className="share-result">
                    <p className="share-ready">Link ready for {meta.companyName}</p>
                    <div className="share-link-row">
                        <input type="text" readOnly value={result.url} onFocus={(e) => e.target.select()} />
                        <button type="button" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
                    </div>
                    <p className="share-meta share-pinned">
                        The prices in this link are pinned to today's calculation settings, so
                        changing them later will not alter a quote already sent.
                    </p>
                    <p className="share-meta">
                        {result.persisted ? (
                            <>Saved as <code>{result.id}</code>. The link stays short and can be revoked later.</>
                        ) : (
                            <>
                                Saved into the link itself — the quote service was not reachable
                                {result.reason ? ` (${result.reason})` : ''}. The link is long but fully self-contained
                                and will keep working.
                            </>
                        )}
                    </p>
                    <div className="share-actions">
                        <a className="share-open" href={result.url} target="_blank" rel="noreferrer">
                            Preview what they see
                        </a>
                        <button type="button" className="share-again" onClick={() => { setStatus('idle'); setResult(null); }}>
                            Create another
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShareQuote;
